<template>
  <c-modal title="Multi-factor Authentication" :show="show" @update:show="toggle" :close-on-backdrop="false">
    <template v-if="setupComplete && processComplete">
      <c-row class="mb-3">
        <c-col sm="12">
          <p class="lead mt-0">
            Excellent! You are all set up and ready to rock.
          </p>
          <p class="lead mt-0">You can Enable MFA now for your account.</p>
          <p class="lead mt-05">
            Return here later to enable/disable MFA for your account or to generate a new MFA code.
          </p>
        </c-col>
      </c-row>
      <c-row class="mb-3">
        <c-col sm="3">
          <label class="label my-1" for="enabled">
            <span><strong>Enable MFA</strong></span>
          </label>
        </c-col>
        <c-col>
          <c-switch
              id="enabled"
              class="float-left"
              color="success"
              :checked="mfaActive"
              shape="pill"
              @update:checked="toggleMfa"
          />
        </c-col>
      </c-row>
    </template>
    <c-row class="mb-3" v-else-if="!setupComplete || changeCode">
      <c-col sm="12">
        <template v-if="!setupComplete && step === 0">
          <p class="lead">MFA is not set up.</p>
          <p class="lead">Click <strong>Start</strong> to begin the setup process.</p>
        </template>
        <template v-if="step === 1">
          <p class="lead">
            Using an
            <strong>Authenticator</strong>
            mobile app, scan this QR code.
          </p>
          <p class="lead">
            After scanning the QR code, you will need to enter the token from your authenticator app.
          </p>
          <vue-qr
              v-if="qrCodeText"
              style="width: 200px; margin: 0 auto; display: block"
              class="mt-5"
              logo-src="/img/logos/koala-large.png"
              :text="qrCodeText"
              :size="400"
          />
          <p class="lead mt-5">
            Or you can enter this code manually into your Authenticator mobile app:
          </p>
          <p class="lead font-weight-bold" style="font-family: Courier,serif">{{secret}}</p>
        </template>
        <template v-if="step === 2">
          <c-input v-model="token" label="Enter your code" autofocus/>
          <c-alert v-if="tokenChecked && !tokenValid" show color="danger">The entered code does not match. Please try again.</c-alert>
        </template>
      </c-col>
    </c-row>
    <template v-else-if="setupComplete && !changeCode">
      <c-row>
        <c-col sm="3">
          <label class="label my-1" for="enabled">
            <span><strong>Enable MFA</strong></span>
          </label>
        </c-col>
        <c-col>
          <c-switch
              id="enabled"
              class="float-left"
              color="success"
              :checked="mfaActive"
              shape="pill"
              @update:checked="toggleMfa"
          />
        </c-col>
      </c-row>
    </template>
    <template v-slot:footer>
      <template v-if="setupComplete && !changeCode">
        <div class="mfa-footer" style="display: flex;">
          <c-button color="secondary" @click="close">Close</c-button>
          <c-button v-if="!processComplete || changeCode" color="primary" @click="change">Change Code</c-button>
        </div>
      </template>
      <template v-else>
        <div class="mfa-footer" v-if="step === 0" style="display: flex;">
          <c-button v-if="!isRequesting" color="secondary" @click="close">Cancel</c-button>
          <c-button color="primary" @click="step1">
            <c-spinner v-if="isRequesting" color="success" size="sm" />
            <span v-else>Start</span>
          </c-button>
        </div>
        <div class="mfa-footer" v-if="step === 1">
          <c-button v-if="!isRequesting" color="secondary" @click="step = 0">Back</c-button>
          <c-button color="primary" @click="step2">
            <c-spinner v-if="isRequesting" color="success" size="sm" />
            <span v-else>Next: Enter Your Code</span>
          </c-button>
        </div>
        <div class="mfa-footer" v-if="step === 2">
          <c-button v-if="!isRequesting" color="secondary" @click="step = 1">Back</c-button>
          <c-button color="primary" @click="step3">
            <c-spinner v-if="isRequesting" color="success" size="sm" />
            <span v-else>Next: Check Code</span>
          </c-button>
        </div>
        <div class="mfa-footer" v-if="step === 3">
          <c-button color="secondary" @click="close">
            <c-spinner v-if="isRequesting" color="success" size="sm" />
            <span v-else>Close</span>
          </c-button>
        </div>
      </template>
    </template>
  </c-modal>
</template>

<script>
import { authenticator } from 'otplib';
import VueQr from 'vue-qr';
import { buildEndpoint } from '@/libs/buildEndPoint';

export default {
  name: 'UserMfa',
  components: {
    VueQr,
  },
  props: {
    show: Boolean,
  },
  data() {
    return {
      changeCode: false,
      isRequesting: false,
      secret: '',
      token: '',
      tokenValid: false,
      tokenChecked: false,
      stepsComplete: 0,
      step: 0,
      processComplete: true,
    };
  },
  computed: {
    authenticatedUser() {
      return this.$store.state?.app?.authenticatedUser;
    },
    setupComplete() {
      return this.authenticatedUser.security.mfa.setupComplete;
    },
    mfaActive() {
      return this.authenticatedUser.security.mfa.active;
    },
    qrCodeText() {
      return this.secret ? authenticator.keyuri(this.authenticatedUser.username, 'KoalaBoss', this.secret) : '';
    },
  },
  mounted() {
    this.setDefaults();
  },
  watch: {
    show() {
      this.setDefaults();
    },
    step(val) {
      if (val === 0) {
        this.changeCode = false;
      }
    },
  },
  methods: {
    setDefaults() {
      this.token = '';
      this.changeCode = false;
      this.processComplete = false;
      this.tokenChecked = false;
      this.secret = this.authenticatedUser.security.mfa.secret;
    },
    toggle(event) {
      if (!event) {
        this.$emit('close');
      }
    },
    close() {
      this.$emit('close');
    },
    async change() {
      this.changeCode = true;
      this.secret = await this.generateMfaSecret();
      this.step = 1;
    },
    async step1() {
      this.secret = await this.generateMfaSecret();
      this.step = 1;
    },
    async step2() {
      this.step = 2;
    },
    async step3() {
      await this.checkMfaToken();
      if (this.tokenValid) {
        this.step = 3;
      }
      this.tokenChecked = true;
    },
    async toggleMfa() {
      this.secret = '';
      const newStatus = !this.authenticatedUser.security.mfa.active;
      const data = {
        mfa: newStatus,
      };
      this.$http
        .put(buildEndpoint('account/security/mfa/toggle'), data)
        .then(async (response) => {
          this.error = null;
          if (response.data.success) {
            this.$store.commit('updateUserMFAActive', newStatus);
            if (newStatus) {
              this.secret = await this.generateMfaSecret();
            }
            this.$emit('updated', {
              mfaStatus: newStatus,
              secret: this.secret,
            });
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: 'Error toggling MFA status',
            });
          }
        })
        .catch((error) => {
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: 'Error toggling MFA status',
          });
        });
    },
    async generateMfaSecret() {
      try {
        const response = await this.$http.get(buildEndpoint('account/security/mfa/generate-secret'));
        return response.data.data;
      } catch (error) {
        this.$store.commit('addErrorMessage', {
          header: 'Error',
          message: 'Error toggling MFA status',
        });
      }
    },
    async checkMfaToken() {
      try {
        this.tokenChecked = false;
        const response = await this.$http.get(`${buildEndpoint('account/security/mfa/check')}?token=${this.token}&setComplete=1`);
        this.tokenValid = response.data.data.tokenValid;
        if (this.tokenValid) {
          this.$store.commit('updateUserMFACompleteStatus', true);
          this.processComplete = true;
        }
      } catch (error) {
        this.$store.commit('addErrorMessage', {
          header: 'Error',
          message: 'Error checking MFA token',
        });
      }
    },
  },
};
</script>

<style lang="css">
  .mfa-footer {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }
</style>
